import { DOM } from 'aurelia-pal';

/**
 * Copy from aurelia validation get-target-dom-element.ts
 *
 * Gets the DOM element associated with the data-binding. Most of the time it's
 * the binding.target but sometimes binding.target is an aurelia custom element,
 * or custom attribute which is a javascript "class" instance, so we need to use
 * the controller's container to retrieve the actual DOM element.
 *
 * Note: York - 2019-1-25
 * 1. Fix the problem when binding.target is containerless. When binding.target
 * is containerless custom element, element got from controller's container is a
 * `#comment` elment. We need an extra `elementSelector` to query for the proper
 * descendant element of this `#comment` element's **parentNode**.
 * 2. Can find a descendant element of the `binding.target` which matches the
 * `elementSelector`. @see {@link ValidateCustomElementBindingBehavior}
 */
export async function getTargetDOMElement(
	binding: any,
	view: any,
	elementSelector?: string,
): Promise<Element> {
	const target = binding.target;
	// DOM element
	if (target instanceof Element) {
		return queryBySelector(target, elementSelector);
	}
	// custom element or custom attribute
	// tslint:disable-next-line:prefer-const
	for (let i = 0, ii = view.controllers.length; i < ii; i++) {
		const controller: any = view.controllers[i];
		if (controller.viewModel === target) {
			let element = controller.container.get(DOM.Element);
			if (controller.behavior && controller.behavior.containerless) {
				// element is an #comment DOM element if containerless
				// note that its parentElement is null but parentNode works fine
				element = element.parentNode.querySelector(elementSelector);
			}
			if (element) {
				return queryBySelector(element, elementSelector);
			}
			throw new Error(
				`Unable to locate target element for "${binding.sourceExpression}".`,
			);
		}
	}
	throw new Error(
		`Unable to locate target element for "${binding.sourceExpression}".`,
	);
}

function queryBySelector(
	target: Element,
	elementSelector: string,
): Promise<Element> {
	if (elementSelector) {
		return new Promise((resolve) => {
			// This is used to locate the input target for the Autocomplete React component which
			// has not finished rendering at the time we bind the validation behaviour to the element
			window.requestAnimationFrame(() => {
				const elem = target.matches(elementSelector)
					? target
					: target.querySelector(elementSelector);
				/**
				 * NOTE(Albert): 2021-03-22 - WEBUI-3930
				 * If an element selector is provided, we expect the target to either have this class or hold
				 * a child that has this class. If a child element does not have this class, elem will be
				 * undefined. Return undefined instead of the target because we do not want to bind validation
				 * in this case. This can occur on `Investment Profile` forms where the `Investment Option`
				 * autocomplete field renders multiple times on changes to the `Redemption Type` field.
				 * First render does not contain correct child elements but subsequent renders do.
				 */
				return resolve(elem);
			});
		});
	}
	return Promise.resolve(target);
}
